import { Alert, FormControl, FormHelperText, Grid, Skeleton, TextField, Typography } from '@mui/material';

import './CCForm.css';
import { useEffect, useRef, useState } from 'react';
import usePayFields from '~/modules/payment/hooks/usePayFields';
import { useOnboardCustomerMutation } from '~/services/member';
import { useOnboardMerchantMutation } from '~/services/location';
import usePayrixIds from '../../hooks/usePayrixIds';
import { useSearchParams } from 'react-router-dom';
import { Controller, useFormContext } from 'react-hook-form';
import { text } from '~/modules/@shared/helpers/text';
import { useGetIds } from '~/modules/auth';

type Props = {
  setPaymentSubmit: Function;
  locationId?: number;
  memberId?: number;
};

export function CCForm({ setPaymentSubmit, locationId, memberId }: Props) {
  const [queryParameters] = useSearchParams();
  const [askForPayrixId, setAskForPayrixId] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  if (!locationId || !memberId) {
    ({ locationId, memberId } = useGetIds());
  }

  useEffect(() => {
    setAskForPayrixId(!!queryParameters.get('PAYRIX_ID'));
  }, [queryParameters]);

  const fieldIds = {
    number: 'card_number',
    cvv: 'security_code',
    expiration: 'expiration_date',
    customer_id: 'customer_id',
  };

  const resolvePay = useRef<null | Function>(null);
  const rejectPay = useRef<null | Function>(null);

  const [isOnboarded, setIsOnboarded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState('');

  const { merchantId, customerId, isPending: isPayrixIdsPending } = usePayrixIds({ memberId });

  const { mutateAsync: onboardMerchant } = useOnboardMerchantMutation({ locationId });
  const { mutateAsync: onboardCustomer } = useOnboardCustomerMutation({ locationId });

  const shouldTriggerError = queryParameters.get('cc_error') === 'true';

  useEffect(() => {
    if (memberId && onboardMerchant && onboardCustomer && !isPayrixIdsPending) {
      const onboardMerchantAndCustomer = async ({
        merchantId,
        customerId,
      }: {
        merchantId: string;
        customerId: string;
      }) => {
        console.warn('onboardMerchantAndCustomer', { memberId, merchantId, customerId });

        try {
          const responseMerchant = await onboardMerchant({
            payrix_merchant_id: merchantId,
            payrix_onboarding_status: 3,
          });

          const responseCustomer = await onboardCustomer({
            payrix_customer_id: customerId,
            payrix_onboarding_status: 3,
            memberId,
          });

          return new Promise((resolve) =>
            resolve({
              responseMerchant,
              responseCustomer,
            }),
          );
        } catch (e) {
          return new Promise((resolve, reject) => {
            resolve;
            reject(e);
          });
        }
      };

      if (customerId) {
        setIsOnboarded(true);
      } else {
        setIsOnboarded(false);
        setError((error) => `${error} No Payrix customer ID, please apply to onboarding into Payrix.`.trim());

        if (askForPayrixId) {
          let newMerchantId = '';
          do {
            newMerchantId = prompt('Please add new MERCHANT ID') ?? '';
          } while (!newMerchantId);

          let newCustomerId = '';
          do {
            newCustomerId = prompt('Please add new CUSTOMER ID') ?? '';
          } while (!newCustomerId);

          onboardMerchantAndCustomer({
            merchantId: newMerchantId ?? '',
            customerId: newCustomerId ?? '',
          })
            .then(({ responseMerchant, responseCustomer }: any) => {
              if (responseMerchant && responseCustomer) {
                setIsOnboarded(true);
                window.location.reload();
              }
            })
            .catch((e: any) => {
              console.error({ e });
            });
        }
      }
    }
  }, [merchantId, customerId, memberId, onboardMerchant, onboardCustomer, isPayrixIdsPending, askForPayrixId]);

  const { isLoaded, submit: PayFieldSubmit } = usePayFields({
    fieldIds,
    customerId,
    merchantId,
    onSuccess: (response: any) => {
      console.log('onSuccess', { response, resolvePay: resolvePay.current });
      resolvePay.current && resolvePay.current()({ response });
    },

    onFailure: (err: any) => {
      console.log('onFailure', { err, rejectPay });
      rejectPay.current && rejectPay.current()({ err });
    },
  });

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        if (!isLoaded || shouldTriggerError) {
          setLoadingTimeout(true);
        }
      },
      shouldTriggerError ? 1000 : 10000,
    );

    return () => clearTimeout(timeoutId);
  }, [isLoaded, shouldTriggerError]);

  useEffect(() => {
    const id = setTimeout(() => {
      if (isLoaded && PayFieldSubmit && setPaymentSubmit) {
        console.log('setting PaymentSubmit');
        setPaymentSubmit(() => () => {
          return new Promise((resolve, reject) => {
            console.log('PROMISE EXECUTED', { resolvePay: resolvePay.current, rejectPay: rejectPay.current });
            resolvePay.current = () => (response: any) => resolve(response);
            rejectPay.current = () => (error: any) => reject(error);
            PayFieldSubmit();
          });
        });
      }
    }, 100);
    return () => clearTimeout(id);
  }, [isLoaded, PayFieldSubmit, setPaymentSubmit]);

  useEffect(() => {
    setIsReady(isLoaded && isOnboarded);
  }, [isLoaded, isOnboarded]);
  const {
    control,
    formState: { errors },
  } = useFormContext();

  if (loadingTimeout) {
    return (
      <Grid item xs={12}>
        <Alert
          variant="outlined"
          severity="error"
          sx={{ backgroundColor: '#FEE', borderColor: '#A00', display: 'flex', justifyContent: 'center' }}>
          There was an error. Refresh the page and try again.
        </Alert>
      </Grid>
    );
  }

  return (
    <>
      {error ? (
        <>
          <Grid item xs={12}>
            <Alert
              variant="outlined"
              severity="error"
              sx={{ backgroundColor: '#FEE', borderColor: '#A00', display: 'flex', justifyContent: 'center' }}>
              Card payment form failed to load, please contact your system administrator. ({error})
            </Alert>
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12} sm={12}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              Card number
            </Typography>
            <div style={{ display: isReady ? '' : 'none' }} id={fieldIds.number} className="form-row"></div>
            {!isReady && <Skeleton variant="rectangular" height={40} />}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              Expiration Date
            </Typography>
            <div style={{ display: isReady ? '' : 'none' }} id={fieldIds.expiration} className="form-row"></div>
            {!isReady && <Skeleton variant="rectangular" height={40} />}
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              Security Code
            </Typography>
            <div style={{ display: isReady ? '' : 'none' }} id={fieldIds.cvv} className="form-row"></div>
            {!isReady && <Skeleton variant="rectangular" height={40} />}
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              Zip Code
            </Typography>
            <Controller
              control={control}
              name="zipcode"
              render={({ field: { value, ...field } }) => (
                <FormControl fullWidth>
                  <TextField
                    {...field}
                    value={value ?? ''}
                    placeholder="123456"
                    error={!!errors.zipcode}
                    helperText={text(errors.zipcode?.message)}
                    FormHelperTextProps={{ error: true }}
                    InputLabelProps={{ shrink: true }}
                  />
                  {!!errors.zipcode && <FormHelperText error>Required</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
          <div id={fieldIds.customer_id} style={{ display: 'none' }}></div>
        </>
      )}
    </>
  );
}
